import f from "./StudentProfileForm.module.css";
import c from "./CurrentPlacementFields.module.css";

/**
 * "Grade & Section" block on the Students → Edit modal. Edits the
 * student's current-term enrollment (same row the Enrollments tab edits),
 * so admins fixing a wrong placement don't have to hunt for that tab.
 *
 * Props:
 *   placement   { enrollmentId, schoolYearId, semesterId, gradeLevelId,
 *                 sectionId, originalSectionId } | null (= not enrolled)
 *   onChange(next)
 *   sections, gradeLevels, schoolYears, semesters   lookup arrays
 *   editable    bool — false renders the placement read-only
 *   disabled    bool
 */
export default function CurrentPlacementFields({
  placement, onChange,
  sections = [], gradeLevels = [], schoolYears = [], semesters = [],
  editable, disabled,
}) {
  if (!placement) {
    return (
      <div className={c.box}>
        <div className={c.head}><span className={c.title}>Grade &amp; Section</span></div>
        <p className={c.muted}>
          Not enrolled in any term yet. Enroll this student from the <b>Enrollments</b> tab.
        </p>
      </div>
    );
  }

  const sy  = schoolYears.find(y => y.id === placement.schoolYearId);
  const sem = semesters.find(x => x.id === placement.semesterId);
  const termLabel = [sy?.label, sem?.label].filter(Boolean).join(" · ");

  const sectionLabel = (id) => {
    const sec = sections.find(x => x.id === id);
    if (!sec) return "—";
    const grade = gradeLevels.find(g => g.id === sec.gradeLevelId);
    return grade ? `${grade.name} · ${sec.name}` : sec.name;
  };

  // Only sections in the enrollment's school year — the backend rejects
  // a section from another year anyway.
  const gradeSections = sections.filter(x =>
    x.schoolYearId === placement.schoolYearId &&
    String(x.gradeLevelId) === String(placement.gradeLevelId));

  function pickGrade(value) {
    const gradeLevelId = value ? Number(value) : "";
    const inGrade = sections.filter(x =>
      x.schoolYearId === placement.schoolYearId && x.gradeLevelId === gradeLevelId);
    // Auto-pick when the grade has exactly one section; otherwise make the
    // admin choose rather than silently keeping a section from another grade.
    onChange({ ...placement, gradeLevelId, sectionId: inGrade.length === 1 ? inGrade[0].id : "" });
  }

  const original = sections.find(x => x.id === placement.originalSectionId);
  const changed  = placement.sectionId && placement.sectionId !== placement.originalSectionId;
  const gradeChanged = changed && original && original.gradeLevelId !== placement.gradeLevelId;

  return (
    <div className={c.box}>
      <div className={c.head}>
        <span className={c.title}>Grade &amp; Section</span>
        {termLabel && <span className={c.term}>{termLabel}</span>}
      </div>

      {!editable ? (
        <p className={c.readOnly}>{sectionLabel(placement.originalSectionId)}</p>
      ) : (
        <>
          <div className={c.row}>
            <div className={f.field}>
              <label className={f.label}>Grade level</label>
              <select className={f.input}
                      value={placement.gradeLevelId ?? ""}
                      onChange={e => pickGrade(e.target.value)}
                      disabled={disabled}>
                <option value="">— pick a grade —</option>
                {gradeLevels.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className={f.field}>
              <label className={f.label}>Section</label>
              <select className={f.input}
                      value={placement.sectionId ?? ""}
                      onChange={e => onChange({ ...placement, sectionId: e.target.value ? Number(e.target.value) : "" })}
                      disabled={disabled || !placement.gradeLevelId}>
                <option value="">
                  {gradeSections.length ? "— pick a section —" : "No sections for this grade"}
                </option>
                {gradeSections.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
          </div>

          {changed && (
            <p className={c.hint}>
              Moving from <b>{sectionLabel(placement.originalSectionId)}</b> to{" "}
              <b>{sectionLabel(placement.sectionId)}</b>.
              {gradeChanged && " This term's tuition invoice will be updated to the new grade's fees (unless payments were already made)."}
            </p>
          )}
        </>
      )}
    </div>
  );
}
